import useIsMobile from '../../hooks/useIsMobile';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import openPdf from '../../api/openPdf';
import { useAuth } from '../../context/AuthContext';
import BudgetVersions from '../../components/budgets/BudgetVersions';
import EmptyState from '../../components/ui/EmptyState';

// ── Config ─────────────────────────────────────────────────────────────────────

const statusConfig = {
  draft:     { label: 'Borrador',  bg: '#F3F4F6', color: '#374151' },
  sent:      { label: 'Enviado',   bg: '#DBEAFE', color: '#1D4ED8' },
  accepted:  { label: 'Aceptado', bg: '#D1FAE5', color: '#065F46' },
  cancelled: { label: 'Cancelado', bg: '#FEE2E2', color: '#991B1B' },
};

const payStatusConfig = {
  pending:   { label: 'Pendiente',  bg: '#FEF3C7', color: '#92400E' },
  validated: { label: 'Validado',   bg: '#D1FAE5', color: '#065F46' },
  rejected:  { label: 'Rechazado',  bg: '#FEE2E2', color: '#991B1B' },
};

const auditEventConfig = {
  created: { label: 'Creado',    bg: '#D1FAE5', color: '#065F46' },
  updated: { label: 'Editado',   bg: '#DBEAFE', color: '#1D4ED8' },
  deleted: { label: 'Eliminado', bg: '#FEE2E2', color: '#991B1B' },
};

const payMethodLabels = { cash: 'Efectivo', transfer: 'Transferencia', check: 'Cheque', card: 'Tarjeta', other: 'Otro' };
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;
const emptyPayment = { amount: '', currency: 'mxn', payment_method: 'transfer', reference: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' };

// ── Mini components ────────────────────────────────────────────────────────────

const Badge = ({ config }) => (
  <span style={{ display: 'inline-flex', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, backgroundColor: config?.bg || '#F3F4F6', color: config?.color || '#374151', whiteSpace: 'nowrap' }}>{config?.label || '—'}</span>
);

const IconBtn = ({ onClick, title, variant = 'default', children, disabled, small }) => {
  const v = { default: ['#F4F6FB','#4B5563','#E5E7EB'], primary: ['#EEF0FB','#4F63D2','#dde1f7'], danger: ['#FEE2E2','#EF4444','#fecaca'], success: ['#D1FAE5','#10B981','#a7f3d0'], warning: ['#FEF3C7','#F59E0B','#fde68a'] }[variant];
  const sz = small ? 26 : 30;
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: sz, height: sz, borderRadius: 7, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', backgroundColor: v[0], color: v[1], opacity: disabled ? 0.5 : 1, flexShrink: 0 }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.backgroundColor = v[2]; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = v[0]; }}>
      {children}
    </button>
  );
};

const inputSt = { width: '100%', padding: '7px 10px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const selectSt = { ...inputSt, cursor: 'pointer' };
const smInput = { ...inputSt, padding: '5px 8px', fontSize: 12 };
const smSelect = { ...smInput, cursor: 'pointer' };

const ConfirmModal = ({ show, title, message, onConfirm, onCancel, confirmLabel = 'Eliminar', danger = true, loading = false }) => {
  if (!show) return null;
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#fff', borderRadius: 14, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: danger ? '#FEE2E2' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
          <svg style={{ width: 22, height: 22, color: danger ? '#EF4444' : '#F59E0B' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h6 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</h6>
        <p style={{ margin: '0 0 22px', fontSize: 13, color: '#6B7280' }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} disabled={loading} style={{ padding: '7px 18px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>Cancelar</button>
          <button onClick={onConfirm} disabled={loading} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', backgroundColor: danger ? '#EF4444' : '#F59E0B', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: loading ? 0.7 : 1 }}>{loading ? 'Procesando...' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const pillSt = { fontSize: 12, color: '#4B5563', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 20, padding: '3px 10px' };

const BudgetPaymentContext = ({ budget, categories, fmt }) => {
  const categoriesWithAmount = categories
    .map(cat => ({ ...cat, items: cat.items.filter(i => Number(i.amount) > 0) }))
    .filter(cat => cat.items.length > 0);
  const hasProcedureInfo = budget.procedure_date || budget.deed_number || budget.volume || budget.tome;
  if (!hasProcedureInfo && categoriesWithAmount.length === 0) return null;

  return (
    <div style={{ background: '#F9FAFB', borderRadius: 9, border: '1px solid #E5E7EB', padding: 14, marginBottom: 14 }}>
      <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 13, color: '#111827' }}>📄 Contexto del presupuesto</p>
      {hasProcedureInfo && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: categoriesWithAmount.length ? 12 : 0 }}>
          {budget.procedure_date && <span style={pillSt}>Fecha: <b>{new Date(budget.procedure_date).toLocaleDateString('es-MX', { timeZone: 'UTC' })}</b></span>}
          {budget.deed_number && <span style={pillSt}>Escritura: <b>{budget.deed_number}</b></span>}
          {budget.volume && <span style={pillSt}>Volumen: <b>{budget.volume}</b></span>}
          {budget.tome && <span style={pillSt}>Tomo: <b>{budget.tome}</b></span>}
        </div>
      )}
      {categoriesWithAmount.map(cat => (
        <div key={cat.id} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{cat.name}</div>
          {cat.items.map(item => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12.5 }}>
              <span style={{ color: '#374151' }}>{budget.language === 'en' && item.name_en ? item.name_en : item.name}</span>
              <span style={{ fontWeight: 600, color: '#111827' }}>{fmt(item.amount)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const PaymentForm = ({ editingPayment, balance, grandTotal, fmt, paymentForm, setPaymentForm, setPaymentVoucher, savingPayment, onSubmit, onCancel }) => (
  <div style={{ background: '#F9FAFB', borderRadius: 9, border: '1px solid #E5E7EB', padding: 14, marginBottom: 14 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#111827' }}>{editingPayment ? 'Editar Pago' : 'Registrar Pago'}</p>
      {!editingPayment && balance > 0 && (
        <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' }}>
          Saldo pendiente: {fmt(balance)}
        </span>
      )}
      {!editingPayment && balance <= 0 && grandTotal > 0 && (
        <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, backgroundColor: '#D1FAE5', color: '#065F46' }}>
          ✓ Presupuesto liquidado
        </span>
      )}
    </div>
    <form onSubmit={onSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '0 12px' }}>
        {[
          { label: 'Monto *', el: <input type="number" min="0.01" step="0.01" required value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} style={smInput} /> },
          { label: 'Forma de pago', el: <select value={paymentForm.payment_method} onChange={e => setPaymentForm(p => ({ ...p, payment_method: e.target.value }))} style={smSelect}><option value="transfer">Transferencia</option><option value="cash">Efectivo</option><option value="check">Cheque</option><option value="card">Tarjeta</option><option value="other">Otro</option></select> },
          { label: 'Fecha *', el: <input type="date" required value={paymentForm.payment_date} onChange={e => setPaymentForm(p => ({ ...p, payment_date: e.target.value }))} style={smInput} /> },
          { label: 'Referencia', el: <input type="text" value={paymentForm.reference} onChange={e => setPaymentForm(p => ({ ...p, reference: e.target.value }))} placeholder="Número de operación" style={smInput} /> },
        ].map(({ label, el }) => (
          <div key={label} style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>{label}</label>
            {el}
          </div>
        ))}
        <div style={{ gridColumn: '1/-1', marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Comprobante {editingPayment && <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(vacío = no cambiar)</span>}</label>
          <input type="file" accept="image/*,application/pdf" onChange={e => setPaymentVoucher(e.target.files[0])} style={{ fontSize: 12 }} />
        </div>
        <div style={{ gridColumn: '1/-1', marginBottom: 10 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 3 }}>Notas</label>
          <textarea rows={2} value={paymentForm.notes} onChange={e => setPaymentForm(p => ({ ...p, notes: e.target.value }))} style={{ ...smInput, resize: 'vertical' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={savingPayment} style={{ padding: '6px 16px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: savingPayment ? 0.7 : 1 }}>{savingPayment ? 'Guardando...' : (editingPayment ? 'Actualizar' : 'Registrar')}</button>
        <button type="button" onClick={onCancel} style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
      </div>
    </form>
  </div>
);

// ── Main ───────────────────────────────────────────────────────────────────────

const BudgetDetail = () => {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const { user } = useAuth();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('items');
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [linkToCaseFile, setLinkToCaseFile] = useState(false);
  const [caseFiles, setCaseFiles] = useState([]);
  const [loadingCaseFiles, setLoadingCaseFiles] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({});
  const [savingItem, setSavingItem] = useState(false);
  const [deleteItemModal, setDeleteItemModal] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);
  const [showAddItem, setShowAddItem] = useState(null);
  const [newItemForm, setNewItemForm] = useState({ name: '', name_en: '', type: 'normal', amount: 0, text_value: '', includes_iva: false, iva_rate: 16 });
  const [addingConceptId, setAddingConceptId] = useState(null);
  const [addingAllMissing, setAddingAllMissing] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [paymentVoucher, setPaymentVoucher] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [deletePaymentModal, setDeletePaymentModal] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [hasUnversionedChanges, setHasUnversionedChanges] = useState(false);
  const [versionsKey, setVersionsKey] = useState(0);

  const fetchBudget = useCallback(async () => {
    try {
      const res = await api.get(`/budgets/${id}`);
      setBudget(res.data);
      setInfoForm({ case_file_id: '', client_name: res.data.client_name || '', client_phone: res.data.client_phone || '', client_email: res.data.client_email || '', client_address: res.data.client_address || '', responsible_name: res.data.responsible_name || '', language: res.data.language, currency: res.data.currency, usd_exchange_rate: res.data.usd_exchange_rate, show_bank_accounts: res.data.show_bank_accounts, notes: res.data.notes || '', status: res.data.status, procedure_type: res.data.procedure_type || '', identifier: res.data.identifier || '', procedure_date: res.data.procedure_date || '', deed_number: res.data.deed_number || '', volume: res.data.volume || '', tome: res.data.tome || '' });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    fetchBudget();
    if (user?.role === 'notary') {
      setLoadingAudit(true);
      api.get(`/activity-log/budgets/${id}`).then(r => setAuditLog(r.data)).catch(console.error).finally(() => setLoadingAudit(false));
    }
  }, [fetchBudget]);

  const handleVersionRestored = useCallback(() => { fetchBudget(); setVersionsKey(k => k + 1); }, [fetchBudget]);

  const fmt = (amount) => {
    const n = Number(amount) || 0;
    if (budget?.currency === 'usd') return `$${(n / (Number(budget.usd_exchange_rate) || 1)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`;
    return `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`;
  };
  const fmtRaw = (a) => Number(a).toLocaleString('es-MX', { minimumFractionDigits: 2 });

  const handleSaveInfo = async () => {
    if (linkToCaseFile && !infoForm.case_file_id) { toast.error('Selecciona un expediente'); return; }
    setSavingInfo(true);
    try {
      const payload = { ...infoForm };
      if (!linkToCaseFile) delete payload.case_file_id;
      await api.put(`/budgets/${id}`, payload);
      toast.success('Actualizado'); setEditingInfo(false); setLinkToCaseFile(false);
      if (!infoForm.status || infoForm.status === budget.status) setHasUnversionedChanges(true);
      fetchBudget();
    }
    catch { toast.error('Error al guardar'); } finally { setSavingInfo(false); }
  };

  const openEditInfo = () => {
    if (!editingInfo && !budget.case_file && caseFiles.length === 0 && !loadingCaseFiles) {
      setLoadingCaseFiles(true);
      api.get('/case-files', { params: { per_page: 100 } }).then(r => setCaseFiles(r.data.data || r.data)).catch(console.error).finally(() => setLoadingCaseFiles(false));
    }
    setEditingInfo(!editingInfo);
  };

  const handleSaveItem = async (itemId) => {
    setSavingItem(true);
    try { await api.put(`/budgets/${id}/items/${itemId}`, itemForm); toast.success('Concepto actualizado'); setEditingItem(null); setHasUnversionedChanges(true); fetchBudget(); }
    catch { toast.error('Error'); } finally { setSavingItem(false); }
  };

  const handleResetAutoFees = async (itemId) => {
    setSavingItem(true);
    try { await api.put(`/budgets/${id}/items/${itemId}`, { reset_auto: true }); toast.success('Vuelto a automático'); setHasUnversionedChanges(true); fetchBudget(); }
    catch { toast.error('Error'); } finally { setSavingItem(false); }
  };

  const handleDeleteItem = async () => {
    if (!deleteItemModal) return; setDeletingItem(true);
    try { await api.delete(`/budgets/${id}/items/${deleteItemModal.id}`); toast.success('Eliminado'); setDeleteItemModal(null); setHasUnversionedChanges(true); fetchBudget(); }
    catch { toast.error('Error'); } finally { setDeletingItem(false); }
  };

  const handleAddItem = async (e, categoryId) => {
    e.preventDefault();
    try { await api.post(`/budgets/${id}/items`, { ...newItemForm, budget_concept_category_id: categoryId }); toast.success('Concepto agregado'); setShowAddItem(null); setNewItemForm({ name: '', name_en: '', type: 'normal', amount: 0, text_value: '', includes_iva: false, iva_rate: 16 }); setHasUnversionedChanges(true); fetchBudget(); }
    catch { toast.error('Error'); }
  };

  const handleAddFromTemplate = async (conceptId) => {
    setAddingConceptId(conceptId);
    try { await api.post(`/budgets/${id}/items/from-template`, { budget_concept_id: conceptId }); toast.success('Concepto agregado'); setHasUnversionedChanges(true); await fetchBudget(); }
    catch { toast.error('Error al agregar'); } finally { setAddingConceptId(null); }
  };

  const handleAddAllMissing = async (concepts) => {
    setAddingAllMissing(true);
    try {
      for (const c of concepts) await api.post(`/budgets/${id}/items/from-template`, { budget_concept_id: c.id });
      toast.success('Conceptos agregados'); setHasUnversionedChanges(true); await fetchBudget();
    } catch { toast.error('Error al agregar'); } finally { setAddingAllMissing(false); }
  };

  const handleMoveItem = async (categoryItems, itemId, direction) => {
    const idx = categoryItems.findIndex(i => i.id === itemId);
    const newItems = [...categoryItems];
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === newItems.length - 1) return;
    const s = direction === 'up' ? idx - 1 : idx + 1;
    [newItems[idx], newItems[s]] = [newItems[s], newItems[idx]];
    try { await api.post(`/budgets/${id}/items/reorder`, { items: newItems.map((item, i) => ({ id: item.id, sort_order: i })) }); fetchBudget(); }
    catch { toast.error('Error al reordenar'); }
  };

  const handleSavePayment = async (e) => {
    e.preventDefault(); setSavingPayment(true);
    try {
      const fd = new FormData();
      Object.entries(paymentForm).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (paymentVoucher) fd.append('voucher', paymentVoucher);
      if (editingPayment) { fd.append('_method', 'PUT'); await api.post(`/budgets/${id}/payments/${editingPayment}`, fd); toast.success('Pago actualizado'); setEditingPayment(null); }
      else { await api.post(`/budgets/${id}/payments`, fd); toast.success('Pago registrado'); setShowPaymentForm(false); }
      setPaymentForm(emptyPayment); setPaymentVoucher(null); fetchBudget();
    } catch { toast.error('Error al guardar pago'); } finally { setSavingPayment(false); }
  };

  const handleValidatePayment = async (paymentId, status) => {
    try { await api.put(`/budgets/${id}/payments/${paymentId}/validate`, { status }); toast.success(status === 'validated' ? 'Validado' : 'Rechazado'); fetchBudget(); }
    catch { toast.error('Error'); }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentModal) return; setDeletingPayment(true);
    try { await api.delete(`/budgets/${id}/payments/${deletePaymentModal.id}`); toast.success('Eliminado'); setDeletePaymentModal(null); fetchBudget(); }
    catch { toast.error('Error'); } finally { setDeletingPayment(false); }
  };

  const handlePrint = (style = 'default') => { openPdf(`/api/budgets/${id}/pdf`, { lang: budget.language || 'es', currency: budget.currency || 'mxn', style }); };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: 14 }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );
  if (!budget) return <div style={{ padding: 16, borderRadius: 10, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Presupuesto no encontrado</div>;

  const allItems = budget.items || [];
  const informativeItems = allItems.filter(i => i.type === 'informative');
  const discountItems = allItems.filter(i => i.type === 'discount');
  const generalItems = allItems.filter(i => i.type === 'general');
  const normalItems = allItems.filter(i => i.type === 'normal');
  const categoriesMap = {};
  normalItems.forEach(item => {
    const k = item.budget_concept_category_id;
    if (!categoriesMap[k]) categoriesMap[k] = { id: k, name: item.concept_category?.name || 'Sin categoría', name_en: item.concept_category?.name_en || '', items: [] };
    categoriesMap[k].items.push(item);
  });
  const categories = Object.values(categoriesMap);
  const addedConceptIds = new Set(allItems.map(i => i.budget_concept_id).filter(Boolean));
  const missingConceptsByCategory = (budget.document_type?.concept_categories || [])
    .map(cat => ({
      id: cat.id,
      name: budget.language === 'en' && cat.name_en ? cat.name_en : cat.name,
      concepts: (cat.concepts || []).filter(c => c.active && !addedConceptIds.has(c.id)),
    }))
    .filter(cat => cat.concepts.length > 0);
  const missingConcepts = missingConceptsByCategory.flatMap(cat => cat.concepts);
  const totals = budget.totals || {};
  const grandTotal = totals.grand_total || 0;
  const totalPaid = totals.total_paid || 0;
  const balance = grandTotal - totalPaid;
  const isNotary = user?.role === 'notary';

  const tabs = [
    { key: 'items',    label: '📋 Conceptos', count: allItems.length },
    { key: 'payments', label: '💳 Pagos',     count: budget.payments?.length || 0 },
    { key: 'versions', label: '🕐 Versiones', dot: hasUnversionedChanges },
    ...(isNotary ? [{ key: 'audit', label: '🔍 Auditoría', count: auditLog.length }] : []),
  ];

  const InfoRow = ({ label, children }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 12, color: '#374151', fontWeight: 600, textAlign: 'right', maxWidth: 160, wordBreak: 'break-word' }}>{children || <span style={{ color: '#D1D5DB' }}>—</span>}</span>
    </div>
  );

  const renderItemRow = (item, categoryItems) => (
    <div key={item.id} style={{ borderBottom: '1px solid #F3F4F6', padding: '10px 14px', opacity: item.visible ? 1 : 0.4, backgroundColor: item.visible ? '#fff' : '#F9FAFB' }}>
      {editingItem === item.id ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, marginBottom: 8 }}>
            <div>
              <input type="text" value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} placeholder="Nombre ES" style={{ ...smInput, marginBottom: 4 }} />
              <input type="text" value={itemForm.name_en} onChange={e => setItemForm({ ...itemForm, name_en: e.target.value })} placeholder="Nombre EN" style={smInput} />
            </div>
            <div>
              <input type="number" value={itemForm.amount} onChange={e => setItemForm({ ...itemForm, amount: e.target.value })} placeholder="Monto" min="0" step="0.01" style={{ ...smInput, marginBottom: 4 }} />
              <div style={{ fontSize: 10, color: '#9CA3AF', paddingLeft: 2 }}>Antes: ${fmtRaw(item.amount)}</div>
              {itemForm.includes_iva && <input type="number" value={itemForm.iva_rate} onChange={e => setItemForm({ ...itemForm, iva_rate: e.target.value })} placeholder="IVA %" style={smInput} />}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4B5563', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={itemForm.includes_iva} onChange={e => setItemForm({ ...itemForm, includes_iva: e.target.checked })} style={{ accentColor: '#4F63D2' }} /> IVA
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4B5563', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={itemForm.visible} onChange={e => setItemForm({ ...itemForm, visible: e.target.checked })} style={{ accentColor: '#4F63D2' }} /> Visible
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => handleSaveItem(item.id)} disabled={savingItem} style={{ padding: '5px 14px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Guardar</button>
            <button onClick={() => setEditingItem(null)} style={{ padding: '5px 12px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>{budget.language === 'en' && item.name_en ? item.name_en : item.name}</span>
            {item.is_fees_concept && (
              item.fees_overridden ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, backgroundColor: '#FEF3C7', color: '#92400E' }}>✎ Manual</span>
                  <button onClick={() => handleResetAutoFees(item.id)} disabled={savingItem}
                    style={{ fontSize: 10.5, fontWeight: 600, color: '#4F63D2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    ↺ Volver a automático
                  </button>
                </span>
              ) : (
                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 20, backgroundColor: '#D1FAE5', color: '#065F46', marginLeft: 8 }}>🧮 Auto (valor mayor)</span>
              )
            )}
            {item.includes_iva && <small style={{ display: 'block', fontSize: 11, color: '#9CA3AF' }}>+ IVA {item.iva_rate}% = ${fmtRaw(item.iva_amount)}</small>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: item.type === 'discount' ? '#EF4444' : '#111827' }}>
              {item.type === 'discount' ? '- ' : ''}${fmtRaw(item.amount)}
              {item.includes_iva && <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 4 }}>+ ${fmtRaw(item.iva_amount)}</span>}
            </span>
            <div style={{ display: 'flex', gap: 3 }}>
              {categoryItems && (
                <>
                  <IconBtn onClick={() => handleMoveItem(categoryItems, item.id, 'up')} title="Subir" small>
                    <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                  </IconBtn>
                  <IconBtn onClick={() => handleMoveItem(categoryItems, item.id, 'down')} title="Bajar" small>
                    <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </IconBtn>
                </>
              )}
              <IconBtn onClick={() => { setEditingItem(item.id); setItemForm({ amount: item.amount, includes_iva: item.includes_iva, iva_rate: item.iva_rate, visible: item.visible, name: item.name, name_en: item.name_en || '' }); }} title="Editar" small>
                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </IconBtn>
              <IconBtn onClick={() => setDeleteItemModal(item)} title="Eliminar" variant="danger" small>
                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </IconBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <ConfirmModal show={!!deleteItemModal} title="Eliminar Concepto" message={`¿Eliminar "${deleteItemModal?.name}" del presupuesto?`} onConfirm={handleDeleteItem} onCancel={() => setDeleteItemModal(null)} loading={deletingItem} />
      <ConfirmModal show={!!deletePaymentModal} title="Eliminar Pago" message={`¿Eliminar el pago de $${fmtRaw(deletePaymentModal?.amount || 0)}?`} onConfirm={handleDeletePayment} onCancel={() => setDeletePaymentModal(null)} loading={deletingPayment} />

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: '#9CA3AF' }}>
        <Link to="/budgets" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Presupuestos</Link>
        <span>›</span>
        <span style={{ color: '#4B5563' }}>{budget.folio || `#${budget.id}`}</span>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h4 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>{budget.folio || `Presupuesto #${budget.id}`}</h4>
            <Badge config={statusConfig[budget.status]} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select value={budget.status}
              onChange={e => {
                const newStatus = e.target.value;
                if (newStatus === 'cancelled') {
                  toast((t) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: 'sans-serif' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>¿Cambiar estado a "Cancelado"?</span>
                      <span style={{ fontSize: 12, color: '#6B7280' }}>Esta acción marcará el presupuesto como cancelado. ¿Confirmas?</span>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => toast.dismiss(t.id)} style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
                        <button onClick={async () => { toast.dismiss(t.id); await api.put(`/budgets/${id}`, { status: newStatus }); toast.success('Estado actualizado'); fetchBudget(); }} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>Confirmar</button>
                      </div>
                    </div>
                  ), { duration: 8000 });
                } else {
                  api.put(`/budgets/${id}`, { status: newStatus }).then(() => { toast.success('Estado actualizado'); fetchBudget(); });
                }
              }}
              style={{ ...selectSt, width: 'auto', fontSize: 12, padding: '4px 8px' }}>
              <option value="draft">Borrador</option>
              <option value="sent">Enviado</option>
              <option value="accepted">Aceptado</option>
              <option value="cancelled">Cancelado</option>
            </select>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>{budget.document_type?.name}</span>
            {budget.case_file && <><span style={{ color: '#D1D5DB' }}>·</span><Link to={`/clients/${budget.case_file.client_id}`} style={{ fontSize: 12, color: '#4F63D2', textDecoration: 'none' }}>{budget.case_file.client?.name}</Link></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* PDF dropdown */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowPdfMenu(p => !p)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              PDF ▾
            </button>
            {showPdfMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowPdfMenu(false)} />
                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1000, minWidth: 160, overflow: 'hidden' }}>
                  {[{ label: '📄 Estilo Clásico', style: 'default' }, { label: '✨ Estilo Elegante', style: 'elegant' }].map(opt => (
                    <button key={opt.style} onClick={() => { handlePrint(opt.style); setShowPdfMenu(false); }}
                      style={{ display: 'block', width: '100%', padding: '10px 14px', textAlign: 'left', border: 'none', backgroundColor: '#fff', fontSize: 13, color: '#374151', cursor: 'pointer', fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <Link to="/budgets" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Regresar
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '240px 1fr', gap: 16, alignItems: 'start' }}>

        {/* ── Panel lateral ─────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: isMobile ? 'static' : 'sticky', top: 20, alignSelf: 'flex-start' }}>

          {/* Info */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>Información</span>
              <IconBtn onClick={openEditInfo} title={editingInfo ? 'Cancelar' : 'Editar'} small>
                {editingInfo
                  ? <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  : <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>}
              </IconBtn>
            </div>
            <div style={{ padding: '10px 14px' }}>
              {editingInfo ? (
                <div>
                  {[
                    { label: 'Idioma', el: <select value={infoForm.language} onChange={e => setInfoForm({ ...infoForm, language: e.target.value })} style={smSelect}><option value="es">Español</option><option value="en">English</option></select> },
                    { label: 'Moneda', el: <select value={infoForm.currency} onChange={e => setInfoForm({ ...infoForm, currency: e.target.value })} style={smSelect}><option value="mxn">MXN</option><option value="usd">USD</option></select> },
                    ...(infoForm.currency === 'usd' ? [{ label: 'Tipo de cambio', el: <input type="number" step="0.0001" value={infoForm.usd_exchange_rate} onChange={e => setInfoForm({ ...infoForm, usd_exchange_rate: e.target.value })} style={smInput} /> }] : []),
                    ...(!budget.case_file ? [
                      { label: 'Cliente', el: (
                        <div>
                          <div style={{ display: 'flex', gap: 5, marginBottom: 8 }}>
                            <button type="button" onClick={() => setLinkToCaseFile(false)}
                              style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: `1.5px solid ${!linkToCaseFile ? '#4F63D2' : '#E5E7EB'}`, backgroundColor: !linkToCaseFile ? '#4F63D2' : '#fff', color: !linkToCaseFile ? '#fff' : '#374151', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                              👤 Público
                            </button>
                            <button type="button" onClick={() => setLinkToCaseFile(true)}
                              style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: `1.5px solid ${linkToCaseFile ? '#4F63D2' : '#E5E7EB'}`, backgroundColor: linkToCaseFile ? '#4F63D2' : '#fff', color: linkToCaseFile ? '#fff' : '#374151', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                              📁 Vincular expediente
                            </button>
                          </div>
                          {linkToCaseFile ? (
                            <select value={infoForm.case_file_id} onChange={e => setInfoForm({ ...infoForm, case_file_id: e.target.value })} style={smSelect} disabled={loadingCaseFiles}>
                              <option value="">{loadingCaseFiles ? 'Cargando expedientes...' : 'Seleccionar expediente...'}</option>
                              {caseFiles.map(cf => <option key={cf.id} value={cf.id}>{cf.client?.name} — {cf.title}</option>)}
                            </select>
                          ) : null}
                        </div>
                      ) },
                    ] : []),
                    ...(!budget.case_file && !linkToCaseFile ? [
                      { label: 'Nombre cliente', el: <input type="text" value={infoForm.client_name} onChange={e => setInfoForm({ ...infoForm, client_name: e.target.value })} style={smInput} /> },
                      { label: 'Teléfono', el: <input type="text" value={infoForm.client_phone} onChange={e => setInfoForm({ ...infoForm, client_phone: e.target.value })} style={smInput} /> },
                      { label: 'Correo', el: <input type="email" value={infoForm.client_email} onChange={e => setInfoForm({ ...infoForm, client_email: e.target.value })} style={smInput} /> },
                      { label: 'Dirección', el: <input type="text" value={infoForm.client_address} onChange={e => setInfoForm({ ...infoForm, client_address: e.target.value })} style={smInput} /> },
                    ] : []),
                    { label: 'Responsable', el: <input type="text" value={infoForm.responsible_name} onChange={e => setInfoForm({ ...infoForm, responsible_name: e.target.value })} style={smInput} /> },
                    { label: 'Tipo de Trámite', el: <input type="text" value={infoForm.procedure_type} onChange={e => setInfoForm({ ...infoForm, procedure_type: e.target.value })} style={smInput} /> },
                    { label: 'Identificador', el: <input type="text" value={infoForm.identifier} onChange={e => setInfoForm({ ...infoForm, identifier: e.target.value })} style={smInput} /> },
                    { label: 'Fecha', el: <input type="date" value={infoForm.procedure_date} onChange={e => setInfoForm({ ...infoForm, procedure_date: e.target.value })} style={smInput} /> },
                    { label: 'Escritura', el: <input type="text" value={infoForm.deed_number} onChange={e => setInfoForm({ ...infoForm, deed_number: e.target.value })} style={smInput} /> },
                    { label: 'Volumen', el: <input type="text" value={infoForm.volume} onChange={e => setInfoForm({ ...infoForm, volume: e.target.value })} style={smInput} /> },
                    { label: 'Tomo', el: <input type="text" value={infoForm.tome} onChange={e => setInfoForm({ ...infoForm, tome: e.target.value })} style={smInput} /> },
                    { label: 'Notas', el: <textarea rows={2} value={infoForm.notes} onChange={e => setInfoForm({ ...infoForm, notes: e.target.value })} style={{ ...smInput, resize: 'vertical' }} /> },
                  ].map(({ label, el }) => (
                    <div key={label} style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#9CA3AF', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
                      {el}
                    </div>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4B5563', cursor: 'pointer', marginBottom: 12 }}>
                    <input type="checkbox" checked={infoForm.show_bank_accounts} onChange={e => setInfoForm({ ...infoForm, show_bank_accounts: e.target.checked })} style={{ accentColor: '#4F63D2' }} />
                    Mostrar cuentas bancarias
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={handleSaveInfo} disabled={savingInfo} style={{ padding: '5px 14px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>{savingInfo ? '...' : 'Guardar'}</button>
                    <button onClick={() => { setEditingInfo(false); setLinkToCaseFile(false); }} style={{ padding: '5px 10px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                    {[budget.language === 'es' ? 'ES' : 'EN', budget.currency?.toUpperCase()].map(t => (
                      <span key={t} style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, backgroundColor: '#F3F4F6', color: '#374151' }}>{t}</span>
                    ))}
                  </div>
                  <InfoRow label="Cliente">
                    {budget.case_file
                      ? <Link to={`/clients/${budget.case_file.client_id}`} style={{ color: '#4F63D2', textDecoration: 'none' }}>{budget.case_file.client?.name}</Link>
                      : budget.client_name || 'Público en general'}
                  </InfoRow>
                  {budget.responsible_name && <InfoRow label="Responsable">{budget.responsible_name}</InfoRow>}
                  {budget.procedure_type && <InfoRow label="Tipo de Trámite">{budget.procedure_type}</InfoRow>}
                  {budget.identifier && <InfoRow label="Identificador">{budget.identifier}</InfoRow>}
                  {budget.procedure_date && <InfoRow label="Fecha">{new Date(budget.procedure_date).toLocaleDateString('es-MX', { timeZone: 'UTC' })}</InfoRow>}
                  {budget.deed_number && <InfoRow label="Escritura">{budget.deed_number}</InfoRow>}
                  {budget.volume && <InfoRow label="Volumen">{budget.volume}</InfoRow>}
                  {budget.tome && <InfoRow label="Tomo">{budget.tome}</InfoRow>}
                  {budget.currency === 'usd' && <InfoRow label="T/C">${budget.usd_exchange_rate} MXN/USD</InfoRow>}
                  <InfoRow label="Creado">{new Date(budget.created_at).toLocaleDateString('es-MX')}</InfoRow>
                  <InfoRow label="Por">{budget.created_by?.name}</InfoRow>
                  {budget.notes && <div style={{ marginTop: 10, padding: '8px', borderRadius: 7, backgroundColor: '#F9FAFB', fontSize: 12, color: '#4B5563', lineHeight: 1.5 }}>{budget.notes}</div>}
                </>
              )}
            </div>
          </div>

          {/* Resumen totales */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 13, color: '#111827' }}>Resumen</div>
            <div style={{ padding: '8px 14px' }}>
              {Object.entries(totals.subtotals || {}).map(([catId, sub]) => {
                const cat = categories.find(c => String(c.id) === String(catId));
                return cat ? (
                  <div key={catId} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #F9FAFB' }}>
                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>{cat.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{fmt(sub)}</span>
                  </div>
                ) : null;
              })}
              {discountItems.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #F9FAFB' }}>
                  <span style={{ fontSize: 12, color: '#EF4444' }}>Descuentos</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>- {fmt(totals.total_discounts || 0)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px', borderTop: '2px solid #E5E7EB', marginTop: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>TOTAL</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#4F63D2' }}>{fmt(grandTotal)}</span>
              </div>
              {totalPaid > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>Pagado</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#10B981' }}>- {fmt(totalPaid)}</span>
                </div>
              )}
              {balance > 0 && totalPaid > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderTop: '1px solid #F3F4F6' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#EF4444' }}>Saldo</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#EF4444' }}>{fmt(balance)}</span>
                </div>
              )}
              {/* Barra de progreso de cobro */}
              {grandTotal > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #F3F4F6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cobrado</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: totalPaid >= grandTotal ? '#065F46' : '#4F63D2' }}>
                      {Math.min(100, Math.round((totalPaid / grandTotal) * 100))}%
                    </span>
                  </div>
                  <div style={{ height: 6, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min(100, Math.round((totalPaid / grandTotal) * 100))}%`,
                      background: totalPaid >= grandTotal ? '#10B981' : '#4F63D2',
                      borderRadius: 4, transition: 'width 0.4s',
                    }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Contenido principal ───────────────────────────── */}
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid #E5E7EB', overflowX: 'auto' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #4F63D2' : '2px solid transparent', marginBottom: -2, backgroundColor: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500, color: activeTab === tab.key ? '#4F63D2' : '#6B7280', whiteSpace: 'nowrap', position: 'relative' }}>
                {tab.label}
                {tab.count !== undefined && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 20, backgroundColor: activeTab === tab.key ? '#EEF0FB' : '#F3F4F6', color: activeTab === tab.key ? '#4F63D2' : '#9CA3AF' }}>{tab.count}</span>}
                {tab.dot && <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#F59E0B', border: '1.5px solid #fff', position: 'absolute', top: 8, right: 6 }} />}
              </button>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', border: '1px solid #E5E7EB', borderTop: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

            {/* ── CONCEPTOS ─────────────────────────────────── */}
            {activeTab === 'items' && (
              <div style={{ padding: 16 }}>
                {/* Banner "sin versionar" — al inicio, no al fondo */}
                {hasUnversionedChanges && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 9, backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', marginBottom: 16 }}>
                    <span style={{ fontSize: 13, color: '#92400E' }}><strong>🕐 Cambios sin versionar</strong> — guarda una versión para registrar estos cambios.</span>
                    <button onClick={() => setActiveTab('versions')} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', backgroundColor: '#F59E0B', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', flexShrink: 0, marginLeft: 10 }}>Guardar versión</button>
                  </div>
                )}
                {/* Datos generales (operación, inmueble, solicitante, etc.) */}
                {generalItems.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Datos generales</p>
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: 9, overflow: 'hidden' }}>
                      {generalItems.map(item => (
                        <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid #F3F4F6' }}>
                          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, flexShrink: 0 }}>
                            {budget.language === 'en' && item.name_en ? item.name_en : item.name}
                          </span>
                          {editingItem === item.id ? (
                            <div style={{ display: 'flex', gap: 4, flex: 1, justifyContent: 'flex-end' }}>
                              <input type="text" value={itemForm.text_value || ''} onChange={e => setItemForm({ ...itemForm, text_value: e.target.value })} style={{ ...smInput, maxWidth: 220 }} autoFocus />
                              <button onClick={() => handleSaveItem(item.id)} disabled={savingItem} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontSize: 12, cursor: 'pointer' }}>✓</button>
                              <button onClick={() => setEditingItem(null)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E5E7EB', backgroundColor: '#fff', fontSize: 12, cursor: 'pointer' }}>✕</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', textAlign: 'right' }}>
                                {item.text_value || <span style={{ color: '#D1D5DB', fontWeight: 500 }}>—</span>}
                              </span>
                              <IconBtn onClick={() => { setEditingItem(item.id); setItemForm({ text_value: item.text_value || '', visible: item.visible, name: item.name, name_en: item.name_en || '' }); }} title="Editar" small>
                                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </IconBtn>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Valores informativos */}
                {informativeItems.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Valores informativos</p>
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: 9, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                          {informativeItems.map(item => (
                            <th key={item.id} style={{ padding: '8px 14px', fontSize: 11, fontWeight: 600, color: '#6B7280', textAlign: 'center', borderBottom: '1px solid #F3F4F6' }}>
                              {budget.language === 'en' && item.name_en ? item.name_en : item.name}
                            </th>
                          ))}
                          <th style={{ width: 60 }}></th>
                        </tr></thead>
                        <tbody><tr>
                          {informativeItems.map(item => (
                            <td key={item.id} style={{ padding: '10px 14px', textAlign: 'center' }}>
                              {editingItem === item.id ? (
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                  <input type="number" min="0" step="0.01" value={itemForm.amount} onChange={e => setItemForm({ ...itemForm, amount: e.target.value })} style={{ ...smInput, width: 100 }} />
                                  <button onClick={() => handleSaveItem(item.id)} disabled={savingItem} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontSize: 12, cursor: 'pointer' }}>✓</button>
                                  <button onClick={() => setEditingItem(null)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E5E7EB', backgroundColor: '#fff', fontSize: 12, cursor: 'pointer' }}>✕</button>
                                </div>
                              ) : <span style={{ fontSize: 13, fontWeight: 700 }}>{fmt(item.amount)}</span>}
                            </td>
                          ))}
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            {informativeItems.map(item => editingItem !== item.id && (
                              <IconBtn key={item.id} onClick={() => { setEditingItem(item.id); setItemForm({ amount: item.amount, includes_iva: item.includes_iva, iva_rate: item.iva_rate, visible: item.visible, name: item.name, name_en: item.name_en || '' }); }} title="Editar" small>
                                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </IconBtn>
                            ))}
                          </td>
                        </tr></tbody>
                      </table>
                    </div>
                  </div>
                )}

                {allItems.length === 0 && <EmptyState icon="📋" title="Sin conceptos en este presupuesto" />}

                {missingConcepts.length > 0 && (
                  <div style={{ marginBottom: 20, padding: 12, borderRadius: 9, backgroundColor: '#EEF0FB', border: '1px solid #dde1f7' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#4F63D2' }}>
                        {missingConcepts.length === 1 ? 'Hay 1 concepto de la plantilla que no está en este presupuesto' : `Hay ${missingConcepts.length} conceptos de la plantilla que no están en este presupuesto`}
                      </span>
                      <button onClick={() => handleAddAllMissing(missingConcepts)} disabled={addingAllMissing}
                        style={{ padding: '5px 12px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: addingAllMissing ? 'not-allowed' : 'pointer', opacity: addingAllMissing ? 0.7 : 1 }}>
                        {addingAllMissing ? 'Agregando...' : 'Agregar todos'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {missingConceptsByCategory.map(cat => (
                        <div key={cat.id}>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#4F63D2', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6, paddingLeft: 2 }}>
                            {cat.name}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {cat.concepts.map(c => (
                              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderRadius: 7, backgroundColor: '#fff' }}>
                                <span style={{ fontSize: 12.5, color: '#374151' }}>
                                  {budget.language === 'en' && c.name_en ? c.name_en : c.name}
                                  <span style={{ color: '#9CA3AF' }}> · {c.type === 'general' ? (c.default_text_value || 'texto') : fmtRaw(c.default_amount)}</span>
                                </span>
                                <button onClick={() => handleAddFromTemplate(c.id)} disabled={addingConceptId === c.id}
                                  style={{ padding: '3px 10px', borderRadius: 6, border: '1.5px solid #4F63D2', backgroundColor: '#fff', color: '#4F63D2', fontWeight: 600, fontSize: 11.5, cursor: addingConceptId === c.id ? 'not-allowed' : 'pointer', flexShrink: 0, marginLeft: 8 }}>
                                  {addingConceptId === c.id ? '...' : '+ Agregar'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {categories.map(cat => (
                  <div key={cat.id} style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{budget.language === 'en' && cat.name_en ? cat.name_en : cat.name}</span>
                      <button onClick={() => { setShowAddItem(cat.id); setNewItemForm({ name: '', name_en: '', type: 'normal', amount: 0, text_value: '', includes_iva: false, iva_rate: 16 }); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 7, border: '1.5px solid #4F63D2', backgroundColor: '#EEF0FB', color: '#4F63D2', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                        <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                        Agregar
                      </button>
                    </div>

                    {showAddItem === cat.id && (
                      <div style={{ background: '#F9FAFB', borderRadius: 9, border: '1px solid #E5E7EB', padding: 12, marginBottom: 10 }}>
                        <form onSubmit={e => handleAddItem(e, cat.id)}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                            <input type="text" value={newItemForm.name} onChange={e => setNewItemForm({ ...newItemForm, name: e.target.value })} placeholder="Nombre ES *" required style={smInput} />
                            <input type="text" value={newItemForm.name_en} onChange={e => setNewItemForm({ ...newItemForm, name_en: e.target.value })} placeholder="Nombre EN" style={smInput} />
                            {newItemForm.type === 'general' ? (
                              <input type="text" value={newItemForm.text_value} onChange={e => setNewItemForm({ ...newItemForm, text_value: e.target.value })} placeholder="Valor" style={smInput} />
                            ) : (
                              <input type="number" value={newItemForm.amount} onChange={e => setNewItemForm({ ...newItemForm, amount: e.target.value })} placeholder="Monto" min="0" step="0.01" style={smInput} />
                            )}
                            <select value={newItemForm.type} onChange={e => setNewItemForm({ ...newItemForm, type: e.target.value })} style={smSelect}>
                              <option value="normal">Normal</option>
                              <option value="informative">Informativo</option>
                              <option value="discount">Descuento</option>
                              <option value="general">General</option>
                            </select>
                          </div>
                          {newItemForm.type !== 'general' && (
                            <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'center' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                                <input type="checkbox" checked={newItemForm.includes_iva} onChange={e => setNewItemForm({ ...newItemForm, includes_iva: e.target.checked })} style={{ accentColor: '#4F63D2' }} /> IVA
                              </label>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button type="submit" style={{ padding: '5px 14px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Agregar</button>
                            <button type="button" onClick={() => setShowAddItem(null)} style={{ padding: '5px 10px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
                          </div>
                        </form>
                      </div>
                    )}

                    <div style={{ border: '1px solid #E5E7EB', borderRadius: 9, overflow: 'hidden' }}>
                      {cat.items.map(item => renderItemRow(item, cat.items))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', backgroundColor: '#F9FAFB' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#9CA3AF' }}>Subtotal</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#4F63D2' }}>{fmt(totals.subtotals?.[cat.id] || 0)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {discountItems.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#EF4444', display: 'block', marginBottom: 8 }}>Descuentos</span>
                    <div style={{ border: '1px solid #FECACA', borderRadius: 9, overflow: 'hidden' }}>
                      {discountItems.map(item => renderItemRow(item, null))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', backgroundColor: '#FEF2F2' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>Total descuentos</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#EF4444' }}>- {fmt(totals.total_discounts || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {(categories.length > 0 || discountItems.length > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderRadius: 10, backgroundColor: '#EEF0FB', border: '1px solid #C7D2F8', marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>TOTAL</span>
                    <span style={{ fontWeight: 800, fontSize: 20, color: '#4F63D2', letterSpacing: '-0.5px' }}>{fmt(grandTotal)}</span>
                  </div>
                )}

              </div>
            )}

            {/* ── PAGOS ─────────────────────────────────────── */}
            {activeTab === 'payments' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Pagos</span>
                  {!showPaymentForm && !editingPayment && (
                    <button onClick={() => { setShowPaymentForm(true); setPaymentForm(emptyPayment); setPaymentVoucher(null); }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, border: 'none', cursor: 'pointer' }}>
                      <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                      Registrar Pago
                    </button>
                  )}
                </div>
                <div style={{ padding: 16 }}>
                  <BudgetPaymentContext budget={budget} categories={categories} fmt={fmt} />

                  {showPaymentForm && (
                    <PaymentForm
                      editingPayment={editingPayment} balance={balance} grandTotal={grandTotal} fmt={fmt}
                      paymentForm={paymentForm} setPaymentForm={setPaymentForm} setPaymentVoucher={setPaymentVoucher}
                      savingPayment={savingPayment} onSubmit={handleSavePayment}
                      onCancel={() => { setShowPaymentForm(false); setPaymentForm(emptyPayment); setPaymentVoucher(null); }}
                    />
                  )}

                  {!budget.payments?.length && !showPaymentForm && <EmptyState icon="💳" title="No hay pagos registrados" />}

                  {budget.payments?.map(payment => (
                    <div key={payment.id} style={{ border: '1px solid #E5E7EB', borderRadius: 9, marginBottom: 10, overflow: 'hidden' }}>
                      {editingPayment === payment.id && (
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6' }}>
                          <PaymentForm
                            editingPayment={editingPayment} balance={balance} grandTotal={grandTotal} fmt={fmt}
                            paymentForm={paymentForm} setPaymentForm={setPaymentForm} setPaymentVoucher={setPaymentVoucher}
                            savingPayment={savingPayment} onSubmit={handleSavePayment}
                            onCancel={() => { setEditingPayment(null); setPaymentForm(emptyPayment); setPaymentVoucher(null); }}
                          />
                        </div>
                      )}
                      <div style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                              <Badge config={payStatusConfig[payment.status]} />
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>${fmtRaw(payment.amount)} {payment.currency?.toUpperCase()}</span>
                              <span style={{ fontSize: 12, color: '#9CA3AF' }}>{payMethodLabels[payment.payment_method]}</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#9CA3AF' }}>
                              {new Date(payment.payment_date).toLocaleDateString('es-MX')}
                              {payment.reference && ` · Ref: ${payment.reference}`}
                              {' · '}Registrado por: {payment.registered_by?.name}
                            </div>
                            {payment.notes && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>{payment.notes}</div>}
                            {(payment.voucher_drive_url || payment.voucher_path) && (
                              <a href={payment.voucher_drive_url || `${STORAGE_URL}/${payment.voucher_path}`} target="_blank" rel="noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 12, color: '#4F63D2', textDecoration: 'none', padding: '3px 9px', borderRadius: 6, backgroundColor: '#EEF0FB' }}>
                                {payment.voucher_drive_url ? '📁 Ver en Drive' : '📎 Ver comprobante'}
                              </a>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {isNotary && payment.status === 'pending' && (
                              <>
                                <button onClick={() => handleValidatePayment(payment.id, 'validated')} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>✓ Validar</button>
                                <button onClick={() => handleValidatePayment(payment.id, 'rejected')} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Rechazar</button>
                              </>
                            )}
                            {((isNotary || payment.registered_by?.id === user?.id)) && (
                              <IconBtn onClick={() => { setEditingPayment(payment.id); setPaymentForm({ amount: payment.amount, currency: payment.currency, payment_method: payment.payment_method, reference: payment.reference || '', payment_date: payment.payment_date?.slice(0, 10) || '', notes: payment.notes || '' }); setPaymentVoucher(null); setShowPaymentForm(false); }} title="Editar" small>
                                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </IconBtn>
                            )}
                            {(isNotary || payment.registered_by?.id === user?.id) && (
                              <IconBtn onClick={() => setDeletePaymentModal(payment)} title="Eliminar" variant="danger" small>
                                <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </IconBtn>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(budget.payments?.length > 0 || totalPaid > 0) && (
                    <div style={{ padding: '12px 14px', borderRadius: 9, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', marginTop: 8 }}>
                      {[
                        { label: 'Total del presupuesto', value: fmt(grandTotal), color: '#374151' },
                        { label: 'Total pagado (validado)', value: fmt(totalPaid), color: '#10B981' },
                        { label: balance > 0 ? 'Saldo pendiente' : '✓ Pagado completo', value: balance > 0 ? fmt(balance) : fmt(0), color: balance > 0 ? '#EF4444' : '#10B981', bold: true },
                      ].map(({ label, value, color, bold }) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
                          <span style={{ fontSize: 12, color: color, fontWeight: bold ? 700 : 400 }}>{label}</span>
                          <span style={{ fontSize: 12, fontWeight: bold ? 800 : 600, color }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── VERSIONES ─────────────────────────────────── */}
            {activeTab === 'versions' && (
              <div style={{ padding: 16 }}>
                <BudgetVersions key={versionsKey} budgetId={id} isNotary={isNotary} onRestored={handleVersionRestored} onVersionSaved={() => setHasUnversionedChanges(false)} />
              </div>
            )}

            {/* ── AUDITORÍA ─────────────────────────────────── */}
            {activeTab === 'audit' && isNotary && (
              <>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, fontSize: 14, color: '#111827' }}>Log de Auditoría</div>
                {loadingAudit ? <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div style={{ width: 24, height: 24, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /></div>
                  : !auditLog.length ? <EmptyState icon="🔍" title="Sin registros de auditoría" />
                  : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ backgroundColor: '#F9FAFB' }}>{['Acción', 'Por', 'Cambios', 'Fecha'].map((h, i) => <th key={i} style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6', width: i === 2 ? '45%' : 'auto' }}>{h}</th>)}</tr></thead>
                    <tbody>{auditLog.map(log => (
                      <tr key={log.id} style={{ backgroundColor: '#fff' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                        <td style={{ padding: '10px 14px' }}><Badge config={auditEventConfig[log.event]} /></td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{log.causer}</td>
                        <td style={{ padding: '10px 14px', fontSize: 11, wordBreak: 'break-word' }}>
                          {log.old && log.new ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{Object.keys(log.new).map(k => log.old[k] !== log.new[k] ? <div key={k}><span style={{ color: '#9CA3AF' }}>{k}: </span><span style={{ textDecoration: 'line-through', color: '#EF4444' }}>{String(log.old[k] ?? '—')}</span><span style={{ color: '#9CA3AF' }}> → </span><span style={{ color: '#10B981' }}>{String(log.new[k] ?? '—')}</span></div> : null)}</div> : <span style={{ color: '#9CA3AF' }}>{log.description}</span>}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      </tr>
                    ))}</tbody>
                  </table>}
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetDetail;
